import Color from 'color'

import { getBrowserThemeMode, getForegroundColor } from '#web-oura-tools/theme/utilities'

import { getCategoryThemes } from '#web-oura-tools/theme/themes'
import type { CATEGORY_THEMES, ThemeColors } from '#web-oura-tools/theme/themes'

export type CssVars = Record<string, string>

export interface CssVarsThemes {
  dark: CssVars
  light: CssVars
}

const WHITE = Color('#ffffff')
const BLACK = Color('#000000')

// Amount to mix the base toward white (positive) or black (negative) per shade.
const RAMP: Record<string, number> = {
  '50': 0.95,
  '100': 0.9,
  '200': 0.75,
  '300': 0.6,
  '400': 0.3,
  '500': 0,
  '600': -0.1,
  '700': -0.25,
  '800': -0.4,
  '900': -0.55,
  '950': -0.7,
}

function primaryRamp(base: string): CssVars {
  const c = Color(base)

  return Object.fromEntries(
    Object.entries(RAMP).map(([shade, amount]) => [
      `--color-primary-${shade}`,
      amount >= 0 ? c.mix(WHITE, amount).hex() : c.mix(BLACK, -amount).hex(),
    ]),
  )
}

function toVars(colors: ThemeColors): CssVars {
  const vars = Object.fromEntries(
    Object.entries(colors).flatMap(([name, value]) => [
      [`--color-${name}`, value],
      [`--color-on-${name}`, getForegroundColor(value)],
    ]),
  )

  return { ...vars, ...primaryRamp(colors.primary) }
}

/**
 * Build light and dark themes as CSS custom properties (e.g. for theming Tailwind).
 *
 * @param input - Category name, Material color name, or hex color. Unknown or omitted values fall back to a default color.
 */
export function getTailwindThemes(input?: keyof typeof CATEGORY_THEMES): CssVarsThemes {
  const themes = getCategoryThemes(input)

  return { light: toVars(themes.light), dark: toVars(themes.dark) }
}

/**
 * Apply a CSS custom property theme to an element (e.g. for theming Tailwind).
 *
 * @param input - Category name, Material color name, or hex color. Unknown or omitted values fall back to a default color.
 * @param dark - Whether to apply the dark variant. Defaults to the browser's preferred color scheme.
 */
export function applyTailwindTheme(
  el: HTMLElement,
  input?: keyof typeof CATEGORY_THEMES,
  dark = getBrowserThemeMode() === 'dark',
): void {
  const themes = getCategoryThemes(input)
  const vars = toVars(dark ? themes.dark : themes.light)

  for (const [key, value] of Object.entries(vars)) {
    el.style.setProperty(key, value)
  }

  el.classList.toggle('dark', dark)
}
